import React, { useState } from "react";
import Layout from "../components/Layout";

const Contact = () => {
  // State to handle form data
  const [formData, setFormData] = useState({
    subject: "",
    email: "",
    comment: "",
  });

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Simple validation
    if (!formData.subject || !formData.email || !formData.comment) {
      alert("All fields are required.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subject: formData.subject,
          email: formData.email,
          comment: formData.comment,
        }),
      });

      if (response.ok) {
        alert("Your message has been sent successfully!");
        setFormData({
          subject: "",
          email: "",
          comment: "",
        });
      } else {
        const errorData = await response.json();
        alert(`Failed to send your message. ${errorData.message || "Please try again later."}`);
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      alert("An error occurred while sending your message. Please try again later.");
    }
  };

  return (
    <Layout>
      <div className="bg-[#F9FAFB] min-h-screen pb-20">

        {/* Dark Banner Section */}
        <div className="bg-[#0B1120] text-white py-16 px-6 md:px-12 text-center flex flex-col items-center justify-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">Contact Us</h1>
          <p className="text-gray-400 text-sm md:text-base max-w-2xl leading-relaxed">
            Have questions about uHyve or need assistance with your account? Our team is here to help you grow your independent beauty business.
          </p>
        </div>

        {/* Two-Column Content Section */}
        <div className="max-w-7xl mx-auto px-6 mt-12 flex flex-col md:flex-row gap-12 relative">

          {/* Left Column: Sticky Table of Contents */}
          <div className="md:w-1/4 flex-shrink-0">
            <div className="sticky top-32">
              <h3 className="text-[14px] font-bold text-[#0B1120] uppercase tracking-wider mb-6">Legal & Contact</h3>
              <ul className="space-y-4">
                <li>
                  <a href="/privacy-policy" className="text-[15px] text-gray-500 hover:text-[#0B1120] transition-colors border-l-2 border-transparent hover:border-gray-300 pl-3 py-1 block">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms-of-service" className="text-[15px] text-gray-500 hover:text-[#0B1120] transition-colors border-l-2 border-transparent hover:border-gray-300 pl-3 py-1 block">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="/cookie-policy" className="text-[15px] text-gray-500 hover:text-[#0B1120] transition-colors border-l-2 border-transparent hover:border-gray-300 pl-3 py-1 block">
                    Cookie Policy
                  </a>
                </li>
                <li>
                  <a href="/contact" className="text-[15px] font-semibold text-[#7C3AED] hover:text-[#6D28D9] transition-colors border-l-2 border-[#7C3AED] pl-3 py-1 block">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column: Main Content Form */}
          <div className="md:w-3/4">

            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-gray-100 mb-8">
              <h2 className="text-2xl font-bold mb-4 text-[#0B1120]">Our Contact Information</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                If you have any questions or need assistance, feel free to reach out to us directly or use the form below.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                <div className="flex items-start gap-4 bg-gray-50 p-6 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-[#7C3AED]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">DIRECT EMAIL</p>
                    <a href="mailto:info@uhyve.com" className="text-[#0B1120] font-medium hover:text-[#7C3AED] transition-colors block mb-1">info@uhyve.com</a>
                    <p className="text-[13px] text-gray-500">We aim to respond within 24 hours.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gray-50 p-6 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5 text-[#7C3AED]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">BUSINESS HOURS</p>
                    <p className="text-[#0B1120] font-medium block mb-1">Mon-Fri: 9:00 AM - 5:00 PM EST</p>
                    <p className="text-[13px] text-gray-500">Weekend support limited to urgent issues.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-gray-100 mb-12">
              <h2 className="text-2xl font-bold mb-8 text-[#0B1120]">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-[#0B1120] mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="How can we help?"
                      className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 text-[#0B1120] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED] transition-all placeholder:text-gray-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-[#0B1120] mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 text-[#0B1120] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED] transition-all placeholder:text-gray-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="comment" className="block text-sm font-semibold text-[#0B1120] mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="comment"
                    name="comment"
                    value={formData.comment}
                    onChange={handleChange}
                    required
                    placeholder="Tell us what you need..."
                    className="w-full px-4 py-3 bg-[#F9FAFB] border border-gray-200 text-[#0B1120] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED] transition-all placeholder:text-gray-400 resize-none"
                    rows="6"
                  ></textarea>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="px-8 py-3.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold rounded-xl shadow-sm hover:shadow-md transition-all w-full md:w-auto"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Contact;
